<script setup>
// One Stage canonical Badge / Pill primitive.
// Compact status / metadata indicator.
//
// Variants:
//   neutral | brand | success | warning | danger | info
// Styles:
//   subtle (default tinted background) | solid | outline
// Sizes:
//   xs (18 px) | sm (22 px) | md (26 px)
//
// Composition:
//   <Badge>Live</Badge>
//   <Badge variant="success" :dot="true">Online</Badge>
//   <Badge variant="danger" style-variant="solid" icon="i-lucide-circle-alert">Failed</Badge>

import { computed, useAttrs, useSlots } from 'vue';
import Icon from '../icon/Icon.vue';

const props = defineProps({
  variant: {
    type: String,
    default: 'neutral',
    validator: v => ['neutral', 'brand', 'success', 'warning', 'danger', 'info'].includes(v),
  },
  /** Visual style — subtle (tinted) / solid / outline. */
  styleVariant: {
    type: String,
    default: 'subtle',
    validator: v => ['subtle', 'solid', 'outline'].includes(v),
  },
  size: {
    type: String,
    default: 'sm',
    validator: v => ['xs', 'sm', 'md'].includes(v),
  },
  /** Optional leading dot indicator. */
  dot: { type: Boolean, default: false },
  /** Optional leading icon. */
  icon: { type: [String, Object, Function], default: '' },
});

defineOptions({ inheritAttrs: true });

const sizeClasses = computed(() => ({
  xs: 'h-[18px] px-1.5 text-[10px] gap-1',
  sm: 'h-[22px] px-2 text-[11px] gap-1.5',
  md: 'h-[26px] px-2.5 text-xs gap-1.5',
}[props.size]));

const variantClasses = computed(() => {
  const map = {
    subtle: {
      neutral: 'bg-n-alpha-2 text-n-slate-12 border border-n-weak',
      brand:   'bg-n-brand/[0.10] text-n-blue-text border border-n-brand/30',
      success: 'bg-n-teal-9/[0.10] text-n-teal-11 border border-n-teal-9/30',
      warning: 'bg-n-amber-9/[0.10] text-n-amber-11 border border-n-amber-9/30',
      danger:  'bg-n-ruby-9/[0.10] text-n-ruby-11 border border-n-ruby-9/30',
      info:    'bg-n-iris-9/[0.10] text-n-iris-11 border border-n-iris-9/30',
    },
    solid: {
      neutral: 'bg-n-slate-12 text-n-solid-1 border border-transparent',
      brand:   'bg-n-brand text-white border border-transparent',
      success: 'bg-n-teal-9 text-white border border-transparent',
      warning: 'bg-n-amber-9 text-n-slate-12 border border-transparent',
      danger:  'bg-n-ruby-9 text-white border border-transparent',
      info:    'bg-n-iris-9 text-white border border-transparent',
    },
    outline: {
      neutral: 'text-n-slate-12 border border-n-strong',
      brand:   'text-n-blue-text border border-n-brand',
      success: 'text-n-teal-11 border border-n-teal-9',
      warning: 'text-n-amber-11 border border-n-amber-9',
      danger:  'text-n-ruby-11 border border-n-ruby-9',
      info:    'text-n-iris-11 border border-n-iris-9',
    },
  };
  return map[props.styleVariant][props.variant];
});

const dotColor = computed(() => ({
  neutral: 'bg-n-slate-11',
  brand:   'bg-n-brand',
  success: 'bg-n-teal-9',
  warning: 'bg-n-amber-9',
  danger:  'bg-n-ruby-9',
  info:    'bg-n-iris-9',
}[props.variant]));
</script>

<template>
  <span
    :class="[
      'inline-flex items-center font-medium leading-none whitespace-nowrap os-r-pill os-tx-colors',
      sizeClasses,
      variantClasses,
    ]"
  >
    <span v-if="dot" :class="['size-1.5 rounded-full', dotColor]" />
    <Icon v-else-if="icon" :icon="icon" class="size-3.5 flex-shrink-0" />
    <slot />
  </span>
</template>
